// react
import { useEffect } from "react";
//Formik
import { useFormik, Form, FormikProvider } from "formik";

// router
import {
  Link as RouterLink,
  useMatch,
  useNavigate,
  useParams,
} from "react-router-dom";

//MUI Components
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import Stack from "@mui/material/Stack";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import LoadingButton from "@mui/lab/LoadingButton";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Chip from "@mui/material/Chip";
import Avatar from "@mui/material/Avatar";
import FormControlLabel from "@mui/material/FormControlLabel";
import RadioGroup from "@mui/material/RadioGroup";
import Radio from "@mui/material/Radio";
import FormLabel from "@mui/material/FormLabel";
import { Icon } from "@iconify/react";

// custom components, constants and services
import { tagFormValidationSchema } from "src/util/validationSchemas";
import { BaseTable } from "../BaseTable/BaseTable";
import { DateTimePicker } from "../FormikDateTimePicker";

//custom hooks
import { useAccess } from "src/hooks/useAccess.hook";
import { useUser } from "src/hooks/useUser.hook";
import { useMutate, useGetDetail, useList } from "src/hooks/useFetch.hook";
import { useSelectedTeam } from "src/hooks/useSelectedTeam";
import { getTeamUrl, tagIdUrl, tagUrl } from "src/react-query/endPoints";
import { is } from "src/util/is";
import { getTagDefaultValues, tagFormChargersColumns, tagUsersColumn } from "./Tags.constants";
import { Button, Card } from "@mui/material";
import { FEATURE } from "src/util/enums";
import { Loader } from "../Loader";
import { f } from "src/utils/formatTime";
import { useSelector } from "react-redux";

function TagForm() {
  const navigate = useNavigate();
  const { teamId } = useSelectedTeam();
  const isCreateTag = useMatch("/tag/new");
  const { data: userData } = useUser();
  const { tagId } = useParams();
  const hasReadOnlyAccess = useAccess(FEATURE.TAG, "R");

  const isReadOnly = hasReadOnlyAccess && !isCreateTag;

  const { data, isFetching } = useGetDetail("tag", { id: tagId });
  const { data: tagUsers, isFetching: isTagUsersFetching } = useGetDetail(
    "tagsUser",
    { id: tagId }
  );
  const {
    data: tagsChargerList,
    isLoading: isTagsChargerFetching,
    refetch,
  } = useGetDetail("tagsCharger", { id: tagId });
  const { data: parentTagsList } = useList("parentTags");
  const { data: rollup } = useGetDetail("tagRollup", {
    id: tagId,
    disabled: isCreateTag || data?.[0]?.tag_type !== "PARENT",
  });
  const { data: parentChildren } = useGetDetail("tagChildren", {
    id: tagId,
    disabled: isCreateTag || data?.[0]?.tag_type !== "PARENT",
  });
  const { data: parentTransactions } = useGetDetail("tagParentTransactions", {
    id: tagId,
    disabled: isCreateTag || data?.[0]?.tag_type !== "PARENT",
  });

  const { tabOpened, tabOpenedFor,detailsId } = useSelector(
    ({ tabsCurrentlyOpenedFor }) => tabsCurrentlyOpenedFor
  );
  const { formData,formDataOf} = useSelector(
    ({ formData }) => formData
  );
  const { mutate, isLoading } = useMutate("tags");
  const onSubmit = async (tagData, { setSubmitting }) => {
    const mutateObject = {
      url: getTeamUrl(teamId, tagUrl),
      method: "POST",
    };
    if (!is.nullOrUndefined(tagId) && !isCreateTag) {
      mutateObject.method = "PUT";
      mutateObject.url = getTeamUrl(teamId, tagIdUrl(tagId));
    }

    // --- Pre-validation to completely block blind database inserts for max active trans count
    // Skip for Parent (department) tags — they don't use max_active_trans_count.
    if (!tagData.isParent) {
      const rawVal = tagData.max_active_trans_count;
      if (rawVal === "" || rawVal === "-" || rawVal === "+" || isNaN(Number(rawVal))) {
        setFieldTouched("max_active_trans_count", true);
        setSubmitting(false);
        return;
      }
    }

    // Wallet Parent must carry a positive balance — the whole point of this
    // kind is the shared wallet.
    if (tagData.parentKind === "WALLET") {
      const bal = parseFloat(tagData.balance_amount);
      if (!Number.isFinite(bal) || bal <= 0) {
        setFieldTouched("balance_amount", true);
        setSubmitting(false);
        return;
      }
    }
    
    // Format date back to exactly what backend expects instead of raw Date object
    if (tagData.expirydate && tagData.expirydate instanceof Date) {
      tagData.expirydate = f(tagData.expirydate, "yyyy-MM-dd");
    }

    tagData.companyId = teamId;

    // The backend infers everything it needs from `parentKind`. Strip the
    // derived display-only fields so they don't show up in the request body
    // and can't be misread by future controller logic.
    const { tag_type: _ttIgnored, isParent: _ipIgnored, ...payload } = tagData;

    mutate(
      {
        url: mutateObject.url,
        method: mutateObject.method,
        body: payload,
      },
      { onSuccess }
    );
    setSubmitting(false);
  };

  const onSuccess = () => {
    if(formDataOf==="customer" && !is.empty(formData) && tabOpenedFor==="customer" && tabOpened==="tags"){
        !is.empty(detailsId)? navigate(`/customer/${detailsId}`): navigate(`/customer/new`);
        return;
    }  
    navigate("/tag")
  };

  const formik = useFormik({
    initialValues: getTagDefaultValues(teamId, userData?.data?.id),
    validationSchema: tagFormValidationSchema,
    onSubmit,
  });

  const {
    errors,
    touched,
    values,
    handleSubmit,
    isSubmitting,
    getFieldProps,
    resetForm,
    setFieldValue,
    setFieldTouched,
  } = formik;

  // If the selected parent tag has a balance, the child cannot have its own
  // balance — it inherits the parent's at runtime (see OcppTagService on the
  // websocket side). When the chosen parent has no balance, the child is free
  // to set its own.
  const selectedParentTag = (parentTagsList || []).find(
    (p) => p.tag === values.parenttag
  );
  const parentHasBalance =
    !!selectedParentTag &&
    selectedParentTag.balance_amount !== null &&
    selectedParentTag.balance_amount !== undefined &&
    selectedParentTag.balance_amount !== "";
  const inheritedParentBalance = parentHasBalance
    ? selectedParentTag.balance_amount
    : null;

  useEffect(() => {
    if (parentHasBalance && values.balance_amount !== "") {
      setFieldValue("balance_amount", "");
    }
  }, [parentHasBalance, values.balance_amount, setFieldValue]);

  useEffect(() => {
    if (data) {
      const [tag] = data;
      
      // Ensure all fields have proper default values to prevent controlled/uncontrolled warnings
      const isParentTag = tag.tag_type === "PARENT";
      const parentHasOwnBalance =
        tag.balance_amount !== null &&
        tag.balance_amount !== undefined &&
        tag.balance_amount !== "" &&
        Number(tag.balance_amount) > 0;
      const sanitizedTag = {
        tag: tag.tag || "",
        emailId: tag.emailId || "",
        parenttag: tag.parenttag || "",
        expirydate: tag.expirydate ? new Date(tag.expirydate) : null,
        max_active_trans_count: tag.max_active_trans_count || 0,
        note: tag.note || "",
        chargerpointId: tag.chargerpointId || 0,
        companyId: tag.companyId || teamId,
        userId: tag.userId || userData?.data?.id,
        balance_amount: tag.balance_amount || "",
        blocked: tag.blocked === "1" || tag.blocked === 1 || tag.blocked === true,
        in_transaction: tag.in_transaction === "1" || tag.in_transaction === 1 || tag.in_transaction === true,
        isParent: isParentTag,
        parentKind: isParentTag
          ? parentHasOwnBalance
            ? "WALLET"
            : "REPORTING"
          : "NONE",
        tag_type: tag.tag_type || "",
        tagTransactionCount: Number(tag.tagTransactionCount ?? 0),
        childCount: Number(tag.childCount ?? 0),
      };
      
      resetForm({ values: sanitizedTag });
    }
  }, [data, resetForm, teamId, userData?.data?.id]);

  if (isFetching) {
    return <Loader />;
  }

  if (!isCreateTag && is.empty(data)) {
    navigate("/tag");
    return null;
  }

  return (
    <Box sx={{ 
      maxWidth: 1000, 
      mx: 'auto', 
      p: 2,
      background: (theme) => theme.palette.background.default
    }}>
      <FormikProvider value={formik}>
        <Form
          autoComplete="off"
          id="authcodes"
          noValidate
          onSubmit={handleSubmit}
        >
          {/* Header Section */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="h5" sx={{ 
              fontWeight: 600, 
              color: (theme) => theme.palette.primary.main,
              mb: 0.5
            }}>
              {isCreateTag
                ? 'Create New Tag'
                : values.tag_type === 'PARENT'
                ? 'Department Tag Details'
                : 'Tag Details'}
            </Typography>
            <Typography variant="body2" sx={{
              color: (theme) => theme.palette.text.secondary
            }}>
              {isCreateTag
                ? 'Configure and create a new tag for your charging network'
                : values.tag_type === 'PARENT'
                ? 'View this department tag and its children. Tag kind cannot be changed after creation.'
                : 'View and manage tag information and settings'
              }
            </Typography>
          </Box>

          {/* Main Form Content */}
          <Paper sx={{ 
            p: 2, 
            borderRadius: 1,
            boxShadow: (theme) => theme.customShadows.card,
            border: (theme) => `1px solid ${theme.palette.divider}`,
            background: (theme) => theme.palette.background.paper
          }}>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              mb: 2,
              pb: 1,
              borderBottom: (theme) => `1px solid ${theme.palette.divider}`
            }}>
              <Icon icon="eva:credit-card-outline" width={20} height={20} style={{ 
                color: (theme) => theme.palette.primary.main,
                marginRight: '8px'
              }} />
              <Typography variant="h6" sx={{ 
                fontWeight: 500, 
                color: (theme) => theme.palette.primary.main
              }}>
                Tag Configuration
              </Typography>
            </Box>

            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormLabel
                  sx={{
                    fontWeight: 600,
                    color: (theme) => theme.palette.text.primary,
                    fontSize: "0.85rem",
                    mb: 1,
                    display: "block",
                  }}
                >
                  Tag Kind
                </FormLabel>
                <RadioGroup
                  row
                  value={values.parentKind || "NONE"}
                  onChange={(e) => {
                    const next = e.target.value;
                    const prev = values.parentKind;
                    setFieldValue("parentKind", next);
                    const becomingParent =
                      next === "REPORTING" || next === "WALLET";
                    setFieldValue("isParent", becomingParent);
                    if (becomingParent) {
                      // Parent tags can't have their own parent and aren't tied
                      // to a single charger.
                      setFieldValue("parenttag", "");
                      setFieldValue("max_active_trans_count", -1);
                    }
                    // Leaving WALLET (to NONE or REPORTING) clears any balance the
                    // user typed for a Department Wallet — otherwise it leaks into
                    // a PREPAID regular tag or a reporting-only parent.
                    if (prev === "WALLET" && next !== "WALLET") {
                      setFieldValue("balance_amount", "");
                    }
                    if (next === "REPORTING") {
                      // A reporting-only parent never carries a balance or an
                      // expiry — both fields are hidden in this mode. Clear any
                      // stale value the user may have typed in another mode.
                      setFieldValue("balance_amount", "");
                      setFieldValue("expirydate", null);
                    }
                  }}
                >
                  <FormControlLabel
                    value="NONE"
                    control={<Radio size="small" />}
                    // Tag kind is permanent once created — no switching in edit
                    // mode (neither regular ↔ parent nor REPORTING ↔ WALLET).
                    disabled={isReadOnly || !isCreateTag}
                    label="Regular tag (RFID)"
                  />
                  <FormControlLabel
                    value="REPORTING"
                    control={<Radio size="small" />}
                    disabled={isReadOnly || !isCreateTag}
                    label="Parent — Reporting only"
                  />
                  <FormControlLabel
                    value="WALLET"
                    control={<Radio size="small" />}
                    disabled={isReadOnly || !isCreateTag}
                    label="Parent — Wallet (shared balance)"
                  />
                </RadioGroup>
                <Typography
                  variant="caption"
                  sx={{ color: "text.secondary", display: "block", mt: 0.5 }}
                >
                  {values.parentKind === "WALLET"
                    ? "Children inherit this wallet's balance and debit it on every stop. Total spend is tracked on this tag."
                    : values.parentKind === "REPORTING"
                    ? "Children keep their own balance. The total charging cost across all children is rolled up on this tag for reporting."
                    : "Standard RFID/PREPAID tag. Optionally pick a parent below to assign it to a department."}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  required
                  label={values.isParent ? "Department Tag ID" : "RFID Tag ID"}
                  size="small"
                  // Tag ID can only be changed before any reference exists.
                  // Once a child or a transaction (this tag's or a child's)
                  // points at the id_tag, renaming would orphan rows.
                  disabled={
                    isReadOnly ||
                    (!isCreateTag &&
                      (Number(values.tagTransactionCount || 0) > 0 ||
                        Number(values.childCount || 0) > 0))
                  }
                  {...getFieldProps("tag")}
                  error={Boolean(touched.tag && errors.tag)}
                  helperText={
                    (touched.tag && errors.tag) ||
                    (!isCreateTag &&
                    Number(values.tagTransactionCount || 0) > 0
                      ? `Locked: ${values.tagTransactionCount} transaction${
                          Number(values.tagTransactionCount) === 1 ? "" : "s"
                        } already reference this tag${
                          values.tag_type === "PARENT" ? " or its children" : ""
                        }.`
                      : !isCreateTag && Number(values.childCount || 0) > 0
                      ? `Locked: ${values.childCount} child tag${
                          Number(values.childCount) === 1 ? "" : "s"
                        } point at this tag.`
                      : "")
                  }
                  fullWidth
                />
              </Grid>
              {values.parentKind !== "REPORTING" && (
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    label={
                      values.isParent ? "Department Wallet Balance" : "Balance Amount"
                    }
                    size="small"
                    type={parentHasBalance ? "text" : "number"}
                    // Balance is set at creation and locked thereafter for all
                    // tag kinds. Wallet top-ups happen via the separate
                    // updateWalletBalance flow, not from this form.
                    disabled={!isCreateTag || parentHasBalance}
                    {...(parentHasBalance
                      ? { value: `Inherits ₹${inheritedParentBalance} from parent` }
                      : getFieldProps("balance_amount"))}
                    error={Boolean(
                      !parentHasBalance &&
                        touched.balance_amount &&
                        errors.balance_amount
                    )}
                    helperText={
                      parentHasBalance
                        ? `Locked: balance is shared with parent tag '${values.parenttag}'`
                        : values.parentKind === "WALLET"
                        ? "Required. Children inherit and debit this wallet."
                        : isCreateTag &&
                          touched.balance_amount &&
                          errors.balance_amount
                    }
                    InputProps={{
                      startAdornment: parentHasBalance ? null : (
                        <Box sx={{
                          display: 'flex',
                          alignItems: 'center',
                          mr: 1,
                          color: 'text.secondary',
                          fontWeight: 500
                        }}>
                          ₹
                        </Box>
                      )
                    }}
                    fullWidth
                  />
                </Grid>
              )}
              {!values.isParent && (
                <Grid item xs={12} sm={6} md={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Parent Tag</InputLabel>
                    <Select
                      disabled={isReadOnly}
                      label="Parent Tag"
                      {...getFieldProps("parenttag")}
                    >
                      <MenuItem value="">None</MenuItem>
                      {(parentTagsList || [])
                        .filter((p) => p.tag !== values.tag)
                        .map((p) => (
                          <MenuItem key={p.id || p.tag} value={p.tag}>
                            {p.tag}
                            {p.note ? ` — ${p.note}` : ""}
                          </MenuItem>
                        ))}
                    </Select>
                  </FormControl>
                </Grid>
              )}
              {!values.isParent && (
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    inputProps={{ min: -1, step: "1" }}
                    {...getFieldProps("max_active_trans_count")}
                    size="small"
                    type="number"
                    disabled={isReadOnly}
                    label="Max Active Transactions"
                    helperText={
                      touched.max_active_trans_count && errors.max_active_trans_count
                        ? errors.max_active_trans_count
                        : "0=block, negative=unlimited, positive=limit"
                    }
                    error={Boolean(touched.max_active_trans_count && errors.max_active_trans_count)}
                    fullWidth
                  />
                </Grid>
              )}
              {values.parentKind !== "REPORTING" && (
                <Grid item xs={12} sm={6} md={4}>
                  <DateTimePicker
                    size="small"
                    label="Expiry Date"
                    mode="date"
                    minDate={new Date()}
                    value={values.expirydate || null}
                    onChange={(newValue) => setFieldValue("expirydate", newValue)}
                    error={Boolean(touched.expirydate && errors.expirydate)}
                    helperText={
                      (touched.expirydate && errors.expirydate) ||
                      (values.parentKind === "WALLET"
                        ? "When set, all child tags become expired on this date"
                        : "")
                    }
                  />
                </Grid>
              )}
              <Grid item xs={12}>
                <TextField
                  label="Notes"
                  size="small"
                  disabled={isReadOnly}
                  multiline
                  rows={2}
                  {...getFieldProps("note")}
                  placeholder={
                    values.isParent
                      ? "Describe this department (e.g. 'Finance team chargers')..."
                      : "Add any additional notes or comments about this RFID tag..."
                  }
                  fullWidth
                />
              </Grid>
            </Grid>
          </Paper>
          {!isCreateTag && values.tag_type === "PARENT" && rollup && (
            <Paper sx={{
              p: 2,
              mt: 2,
              borderRadius: 1,
              boxShadow: (theme) => theme.customShadows.card,
              border: (theme) => `1px solid ${theme.palette.divider}`,
              background: (theme) => theme.palette.background.paper
            }}>
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                mb: 2,
                pb: 1,
                borderBottom: (theme) => `1px solid ${theme.palette.divider}`
              }}>
                <Icon icon="eva:pie-chart-2-outline" width={20} height={20} style={{
                  color: (theme) => theme.palette.primary.main,
                  marginRight: '8px'
                }} />
                <Typography variant="h6" sx={{
                  fontWeight: 500,
                  color: (theme) => theme.palette.primary.main
                }}>
                  {Number(rollup.balance_amount || 0) > 0
                    ? "Department Wallet"
                    : "Department Rollup"}
                </Typography>
              </Box>
              <Grid container spacing={2}>
                {Number(rollup.balance_amount || 0) > 0 && (
                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      Current Balance
                    </Typography>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 700,
                        color: (theme) => theme.palette.success.main,
                      }}
                    >
                      ₹{Number(rollup.balance_amount || 0).toFixed(2)}
                    </Typography>
                  </Grid>
                )}
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    Total Charged
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    ₹{Number(rollup.totalCharged || 0).toFixed(2)}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    Total Paid
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    ₹{Number(rollup.totalPaid || 0).toFixed(2)}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    Sessions
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {rollup.sessionCount || 0}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    Child Tags
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {rollup.activeChildCount || 0} / {rollup.childCount || 0}
                  </Typography>
                </Grid>
              </Grid>
              <Typography
                variant="caption"
                sx={{ display: 'block', mt: 1, color: 'text.secondary' }}
              >
                {Number(rollup.balance_amount || 0) > 0
                  ? "Current balance is the remaining wallet after every child charging session debits it."
                  : "Rolled up from completed transactions of every child tag."}
              </Typography>
            </Paper>
          )}
          {!isCreateTag &&
            values.tag_type === "PARENT" &&
            Number(rollup?.balance_amount || 0) > 0 && (
              <Paper sx={{
                p: 2,
                mt: 2,
                borderRadius: 1,
                boxShadow: (theme) => theme.customShadows.card,
                border: (theme) => `1px solid ${theme.palette.divider}`,
                background: (theme) => theme.palette.background.paper
              }}>
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  mb: 2,
                  pb: 1,
                  borderBottom: (theme) => `1px solid ${theme.palette.divider}`
                }}>
                  <Icon icon="eva:clock-outline" width={20} height={20} style={{
                    color: (theme) => theme.palette.primary.main,
                    marginRight: '8px'
                  }} />
                  <Typography variant="h6" sx={{
                    fontWeight: 500,
                    color: (theme) => theme.palette.primary.main
                  }}>
                    Balance History ({(parentTransactions || []).length})
                  </Typography>
                </Box>
                {(parentTransactions || []).length === 0 ? (
                  <Box sx={{
                    textAlign: 'center',
                    py: 3,
                    px: 2,
                    borderRadius: 1,
                    border: (theme) => `1px dashed ${theme.palette.divider}`,
                    background: (theme) => theme.palette.action.hover
                  }}>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      No charging activity yet on this wallet.
                    </Typography>
                  </Box>
                ) : (
                  <BaseTable
                    height={Math.min(
                      420,
                      60 + (parentTransactions?.length || 0) * 44
                    )}
                    rows={parentTransactions || []}
                    columns={[
                      {
                        field: "stoptime",
                        headerName: "When",
                        width: 170,
                        renderCell: ({ value, row }) => {
                          const t = value || row?.starttime;
                          return t ? (
                            f(new Date(t), "yyyy-MM-dd HH:mm")
                          ) : (
                            <span style={{ color: "#999" }}>—</span>
                          );
                        },
                      },
                      {
                        field: "tag",
                        headerName: "Child Tag",
                        width: 140,
                        renderCell: ({ value }) => value || "—",
                      },
                      {
                        field: "chargepoint_id",
                        headerName: "Charger",
                        width: 130,
                        renderCell: ({ value, row }) =>
                          value
                            ? `${value}${
                                row?.connectorId ? ` / ${row.connectorId}` : ""
                              }`
                            : "—",
                      },
                      {
                        field: "totalAmountCharged",
                        headerName: "Debit",
                        width: 110,
                        renderCell: ({ value }) =>
                          `₹${Number(value || 0).toFixed(2)}`,
                      },
                      {
                        field: "totalAmountPaid",
                        headerName: "Paid",
                        width: 110,
                        renderCell: ({ value }) =>
                          `₹${Number(value || 0).toFixed(2)}`,
                      },
                      {
                        field: "totalRefund",
                        headerName: "Refund",
                        width: 110,
                        renderCell: ({ value }) => {
                          const n = Number(value || 0);
                          return n > 0 ? (
                            <span style={{ color: "#1976d2" }}>
                              ₹{n.toFixed(2)}
                            </span>
                          ) : (
                            <span style={{ color: "#999" }}>—</span>
                          );
                        },
                      },
                    ]}
                    getRowId={(row) => row.transactionId}
                  />
                )}
                <Typography
                  variant="caption"
                  sx={{ display: 'block', mt: 1, color: 'text.secondary' }}
                >
                  Latest charging sessions that debited this wallet, newest first.
                </Typography>
              </Paper>
            )}
          {!isCreateTag && values.tag_type === "PARENT" && (
            <Paper sx={{
              p: 2,
              mt: 2,
              borderRadius: 1,
              boxShadow: (theme) => theme.customShadows.card,
              border: (theme) => `1px solid ${theme.palette.divider}`,
              background: (theme) => theme.palette.background.paper
            }}>
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                mb: 2,
                pb: 1,
                borderBottom: (theme) => `1px solid ${theme.palette.divider}`
              }}>
                <Icon icon="eva:people-outline" width={20} height={20} style={{
                  color: (theme) => theme.palette.primary.main,
                  marginRight: '8px'
                }} />
                <Typography variant="h6" sx={{
                  fontWeight: 500,
                  color: (theme) => theme.palette.primary.main
                }}>
                  Child Tags ({(parentChildren || []).length})
                </Typography>
              </Box>
              {(parentChildren || []).length === 0 ? (
                <Box sx={{
                  textAlign: 'center',
                  py: 3,
                  px: 2,
                  borderRadius: 1,
                  border: (theme) => `1px dashed ${theme.palette.divider}`,
                  background: (theme) => theme.palette.action.hover
                }}>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    No child tags attached to this department yet.
                  </Typography>
                </Box>
              ) : (
                <BaseTable
                  height={Math.min(420, 60 + (parentChildren?.length || 0) * 48)}
                  rows={parentChildren || []}
                  columns={[
                    {
                      field: "tag",
                      headerName: "Tag",
                      flex: 1,
                      minWidth: 140,
                      renderCell: ({ value, id }) => (
                        <RouterLink
                          to={`/tag/${id}`}
                          style={{
                            color: "#1976d2",
                            textDecoration: "none",
                            fontWeight: 500,
                          }}
                        >
                          {value}
                        </RouterLink>
                      ),
                    },
                    {
                      field: "tag_type",
                      headerName: "Type",
                      width: 110,
                      renderCell: ({ value }) => value || "RFID",
                    },
                    {
                      field: "balance_amount",
                      headerName: "Balance / Spend",
                      width: 160,
                      renderCell: ({ value, row }) => {
                        const hasOwnBalance =
                          value !== null &&
                          value !== undefined &&
                          value !== "" &&
                          Number(value) !== 0;
                        if (hasOwnBalance) {
                          return (
                            <span>₹{Number(value).toFixed(2)} (balance)</span>
                          );
                        }
                        const spent = Number(row?.totalCharged || 0);
                        return (
                          <span style={{ color: spent > 0 ? "inherit" : "#999" }}>
                            ₹{spent.toFixed(2)} (spent)
                          </span>
                        );
                      },
                    },
                    {
                      field: "sessionCount",
                      headerName: "Sessions",
                      width: 100,
                      renderCell: ({ value }) => Number(value || 0),
                    },
                    {
                      field: "expirydate",
                      headerName: "Expiry",
                      width: 150,
                      renderCell: ({ value }) =>
                        value
                          ? f(new Date(value), "yyyy-MM-dd")
                          : <span style={{ color: "#999" }}>—</span>,
                    },
                    {
                      field: "status",
                      headerName: "Status",
                      width: 130,
                      renderCell: ({ row }) => {
                        const blocked =
                          row.blocked === "1" ||
                          row.blocked === 1 ||
                          row.blocked === true;
                        const inTx =
                          row.in_transaction === "1" ||
                          row.in_transaction === 1 ||
                          row.in_transaction === true;
                        const expired =
                          row.expirydate &&
                          new Date(row.expirydate) < new Date();
                        if (blocked) {
                          return (
                            <Chip label="Blocked" size="small" color="error" />
                          );
                        }
                        if (expired) {
                          return (
                            <Chip
                              label="Expired"
                              size="small"
                              sx={{
                                bgcolor: (theme) => theme.palette.grey[200],
                              }}
                            />
                          );
                        }
                        if (inTx) {
                          return (
                            <Chip label="Charging" size="small" color="warning" />
                          );
                        }
                        return (
                          <Chip label="Active" size="small" color="success" />
                        );
                      },
                    },
                  ]}
                  getRowId={(row) => row.id}
                />
              )}
              <Typography
                variant="caption"
                sx={{ display: 'block', mt: 1, color: 'text.secondary' }}
              >
                Prepaid / wallet-inheriting children show remaining balance.
                Postpaid (RFID-only) children show cumulative charging cost.
              </Typography>
            </Paper>
          )}
          {!isCreateTag && values.tag_type !== "PARENT" && (
            <Paper sx={{
              p: 2,
              mt: 2,
              borderRadius: 1,
              boxShadow: (theme) => theme.customShadows.card,
              border: (theme) => `1px solid ${theme.palette.divider}`,
              background: (theme) => theme.palette.background.paper
            }}>
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                mb: 2,
                pb: 1,
                borderBottom: (theme) => `1px solid ${theme.palette.divider}`
              }}>
                <Icon icon="eva:person-outline" width={20} height={20} style={{
                  color: (theme) => theme.palette.primary.main,
                  marginRight: '8px'
                }} />
                <Typography variant="h6" sx={{
                  fontWeight: 500,
                  color: (theme) => theme.palette.primary.main
                }}>
                  Associated EV Driver
                </Typography>
              </Box>

              {!is.empty(tagUsers) ? (
                <Grid container spacing={2}>
                  {tagUsers.map(({ userPk, email, firstName, lastName }, index) => (
                    <Grid item xs={12} sm={6} md={4} key={`${index}-${firstName}-${index}`}>
                      <Box sx={{
                        p: 2,
                        borderRadius: 1,
                        border: (theme) => `1px solid ${theme.palette.divider}`,
                        background: (theme) => theme.palette.background.paper
                      }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          <Avatar sx={{ 
                            background: (theme) => theme.palette.primary.main,
                            mr: 1.5,
                            width: 32,
                            height: 32
                          }}>
                            <Icon icon="eva:person-fill" width={16} height={16} />
                          </Avatar>
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="subtitle1" sx={{ 
                              fontWeight: 600, 
                              fontSize: '0.9rem',
                              color: 'text.primary',
                              mb: 0.25
                            }}>
                              {firstName} {lastName}
                            </Typography>
                            <Typography variant="body2" sx={{ 
                              color: 'text.secondary',
                              fontSize: '0.75rem'
                            }}>
                              ID: {userPk}
                            </Typography>
                          </Box>
                        </Box>
                        <Typography variant="body2" sx={{ 
                          color: 'text.primary',
                          fontSize: '0.8rem'
                        }}>
                          {email}
                        </Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <Box sx={{ 
                  textAlign: 'center', 
                  py: 3,
                  px: 2,
                  borderRadius: 1,
                  border: (theme) => `1px dashed ${theme.palette.divider}`,
                  background: (theme) => theme.palette.action.hover
                }}>
                  <Icon icon="eva:person-outline" width={32} height={32} style={{ 
                    color: 'text.secondary', 
                    marginBottom: '8px' 
                  }} />
                  <Typography variant="body1" sx={{ 
                    color: 'text.primary', 
                    mb: 0.5,
                    fontWeight: 500
                  }}>
                    No EV Driver Associated
                  </Typography>
                  <Typography variant="body2" sx={{ 
                    color: 'text.secondary', 
                    fontSize: '0.8rem'
                  }}>
                    This RFID tag is not currently associated with any EV driver
                  </Typography>
                </Box>
              )}
            </Paper>
          )}
          {!isCreateTag && values.tag_type !== "PARENT" && (
            <Paper sx={{ 
              p: 2, 
              mt: 2, 
              borderRadius: 1,
              boxShadow: (theme) => theme.customShadows.card,
              border: (theme) => `1px solid ${theme.palette.divider}`,
              background: (theme) => theme.palette.background.paper
            }}>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                mb: 2,
                pb: 1,
                borderBottom: (theme) => `1px solid ${theme.palette.divider}`
              }}>
                <Icon icon="eva:charging-outline" width={20} height={20} style={{ 
                  color: (theme) => theme.palette.primary.main,
                  marginRight: '8px'
                }} />
                <Typography variant="h6" sx={{ 
                  fontWeight: 500, 
                  color: (theme) => theme.palette.primary.main
                }}>
                  Attached Chargers
                </Typography>
              </Box>

              {tagsChargerList && tagsChargerList.length > 0 ? (
                <Box sx={{ 
                  borderRadius: 1,
                  border: (theme) => `1px solid ${theme.palette.divider}`,
                  overflow: 'hidden',
                  minHeight: 'auto !important',
                  '& .MuiStack-root': {
                    minHeight: 'auto !important',
                    '& .MuiBox-root': {
                      minHeight: 'auto !important',
                      height: 'auto !important',
                      '& .MuiDataGrid-root': {
                        border: 'none !important',
                        minHeight: 'auto !important',
                        height: 'auto !important',
                        '& .MuiDataGrid-main': {
                          minHeight: 'auto !important',
                        },
                        '& .MuiDataGrid-virtualScroller': {
                          minHeight: 'auto !important',
                        },
                        '& .MuiDataGrid-cell': {
                          padding: '6px 8px !important',
                          fontSize: '0.75rem',
                          minHeight: '28px !important',
                          maxHeight: '28px !important',
                        },
                        '& .MuiDataGrid-columnHeaders': {
                          padding: '0 8px',
                          minHeight: '36px !important',
                        },
                        '& .MuiDataGrid-columnHeader': {
                          fontSize: '0.75rem',
                          fontWeight: 600,
                          minHeight: '36px !important',
                        },
                        '& .MuiDataGrid-row': {
                          minHeight: '28px !important',
                          maxHeight: '28px !important',
                        },
                        '& .MuiDataGrid-footerContainer': {
                          minHeight: '32px !important',
                          maxHeight: '32px !important',
                        }
                      }
                    }
                  }
                }}>
                  <BaseTable
                    height={100}
                    rows={tagsChargerList ?? []}
                    columns={tagFormChargersColumns()}
                    getRowId={(row) => row.charge_box_id}
                    loading={isTagsChargerFetching}
                  />
                </Box>
              ) : (
                <Box sx={{ 
                  textAlign: 'center', 
                  py: 2,
                  px: 2,
                  borderRadius: 1,
                  border: (theme) => `1px dashed ${theme.palette.divider}`,
                  background: (theme) => theme.palette.action.hover
                }}>
                  <Icon icon="eva:charging-outline" width={24} height={24} style={{ 
                    color: 'text.secondary', 
                    marginBottom: '8px' 
                  }} />
                  <Typography variant="body2" sx={{ 
                    color: 'text.secondary', 
                    fontSize: '0.8rem'
                  }}>
                    No Chargers Attached
                  </Typography>
                </Box>
              )}
            </Paper>
          )}
          {/* Action Buttons */}
          <Box sx={{ 
            mt: 2, 
            display: 'flex',
            justifyContent: 'flex-end'
          }}>
            <Stack direction="row" spacing={1}>
              {isReadOnly ? (
                <Button
                  size="small"
                  type="button"
                  variant="contained"
                  component={RouterLink}
                  to="/tag"
                  startIcon={<Icon icon="eva:arrow-back-outline" />}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 500,
                  }}
                >
                  Back
                </Button>
              ) : (
                <>
                  <Button
                    size="small"
                    type="button"
                    variant="outlined"
                    component={RouterLink}
                    to="/tag"
                    startIcon={<Icon icon="eva:close-outline" />}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 500,
                    }}
                  >
                    Cancel
                  </Button>
                  <LoadingButton
                    size="small"
                    type="submit"
                    variant="contained"
                    disabled={isReadOnly}
                    loading={isSubmitting || isLoading}
                    startIcon={<Icon icon="eva:save-outline" />}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 500,
                    }}
                  >
                    {isSubmitting || isLoading ? 'Saving...' : 'Save Changes'}
                  </LoadingButton>
                </>
              )}
            </Stack>
          </Box>
        </Form>
      </FormikProvider>
    </Box>
  );
}

export default TagForm;
